class StickyAddToCart extends HTMLElement {
  static ANIMATION_DURATION = 2000;

  constructor() {
    super();

    this.stickyContainer = null;
    this.stickyButtons = null;
    this.mainAddToCartButton = null;
    this.quantityInput = null;
    this.quantityPlus = null;
    this.quantityMinus = null;
    this.mainQuantityInput = null;
    this.totalPriceElement = null;

    // Overage dropdown elements
    this.overageDropdown = null;
    this.overageDropdownTrigger = null;
    this.overageDropdownMenu = null;
    this.overageDropdownValue = null;
    this.mainOverageDropdown = null;

    this.isVisible = false;
    this.animationTimeout = null;
    this.cleanupTimeout = null;
    this.intersectionObserver = null;
    this.mutationObserver = null;
    this.resizeObserver = null;

    this.buttonTextType = this.dataset.buttonTextType || 'add_to_cart';
    this.scrollTargetSelector = this.dataset.scrollTargetSelector || '.product-options__header';

    this.variantPrice = (parseFloat(this.dataset.variantPrice) || 0) / 100;
    this.currencySymbol = this.dataset.currencySymbol || '$';
    this.unitType = this.dataset.unitType || 'piece';
    this.productId = this.dataset.productId || '';

    this.locale = this.dataset.locale || document.documentElement.lang || 'en-US';

    this.translations = {
      box: this.dataset.tBox || 'Box',
      boxes: this.dataset.tBoxes || 'Boxes',
      sheet: this.dataset.tSheet || 'Sheet',
      sheets: this.dataset.tSheets || 'Sheets',
      piece: this.dataset.tPiece || 'Piece',
      pieces: this.dataset.tPieces || 'Pieces',
      sqFt: this.dataset.tSqFt || 'sq.ft.'
    };
  }

  connectedCallback() {
    this.initializeElements();
    this.setupEventListeners();
    this.setupVariantListener();
    this.setupIntersectionObserver();
    this.setupMutationObserver();
    this.setupResizeObserver();
    this.updateFooterPadding();
    this.updateButtonState();
    this.syncInitialQuantity();
    this.updateTotalPrice();
    this.updateUnitText(this.getQuantity());
  }

  disconnectedCallback() {
    this.cleanup();
  }

  initializeElements() {
    this.stickyContainer = this.querySelector('.sticky-add-to-cart');
    this.stickyButtons = this.querySelectorAll('[data-sticky-add-to-cart-button]');
    this.totalPriceElement = this.querySelector('.calc_total');

    if (!this.stickyContainer || this.stickyButtons.length === 0) {
      console.warn('Sticky ATC: Required elements not found');
      return;
    }

    this.mainAddToCartButton = this.findMainAddToCartButton();

    if (!this.mainAddToCartButton) {
      this.stickyContainer.style.display = 'none';
      return;
    }

    this.quantityInput = this.querySelector('.sticky-atc__quantity-input');
    this.quantityPlus = this.querySelector('.sticky-atc__quantity-plus');
    this.quantityMinus = this.querySelector('.sticky-atc__quantity-minus');

    // Overage dropdown elements
    this.overageDropdown = this.querySelector('.sticky-atc__overage-dropdown');
    this.overageDropdownTrigger = this.querySelector('.sticky-atc__dropdown-trigger');
    this.overageDropdownMenu = this.querySelector('.sticky-atc__dropdown-menu');
    this.overageDropdownValue = this.querySelector('.sticky-atc__dropdown-value');

    this.mainQuantityInput = this.findMainQuantityInput();
    this.mainOverageDropdown = this.findMainOverageDropdown();
  }

  findMainAddToCartButton() {
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (productForm) {
      const mainButton = productForm.querySelector('button[type="submit"][name="add"]');
      if (mainButton) return mainButton;
    }

    const addToCartButtons = document.querySelectorAll('button[type="submit"][name="add"]');
    return addToCartButtons.length > 0 ? addToCartButtons[0] : null;
  }

  findMainQuantityInput() {
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (productForm) {
      return productForm.querySelector('input[name="quantity"]') ||
             productForm.querySelector('[data-quantity-input]');
    }
    return document.querySelector('input[name="quantity"]');
  }

  findMainOverageDropdown() {
    const quantityCalculator = document.querySelector('quantity-calculator');
    if (quantityCalculator) {
      return {
        trigger: quantityCalculator.querySelector('[ref="dropdownTrigger"]'),
        menu: quantityCalculator.querySelector('[ref="dropdownMenu"]'),
        value: quantityCalculator.querySelector('[ref="dropdownValue"]'),
        items: quantityCalculator.querySelectorAll('.quantity-calculator__dropdown-item')
      };
    }
    return null;
  }

  findAddToCartComponent() {
    const productForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (productForm) {
      return productForm.closest('product-form-component')?.querySelector('add-to-cart-component');
    }
    return document.querySelector('add-to-cart-component');
  }

  findFooter() {
    return document.querySelector('footer') || document.querySelector('[data-testid="password-footer"]');
  }

  /**
   * Get the product section element for event listening
   */
  findProductSection() {
    return document.querySelector('.shopify-section[id*="product"]') ||
           document.querySelector('[data-section-type="product"]') ||
           this.closest('.shopify-section');
  }

  setupEventListeners() {
    this.stickyButtons.forEach(button => {
      button.addEventListener('click', (e) => this.handleStickyButtonClick(e));
    });

    if (this.quantityPlus) {
      this.quantityPlus.addEventListener('click', () => this.handleQuantityChange(1));
    }
    if (this.quantityMinus) {
      this.quantityMinus.addEventListener('click', () => this.handleQuantityChange(-1));
    }

    if (this.quantityInput) {
      this.quantityInput.addEventListener('input', () => {
        this.syncQuantityToMain();
        this.updateTotalPrice();
      });
    }

    if (this.mainQuantityInput) {
      this.mainQuantityInput.addEventListener('input', () => {
        this.syncQuantityFromMain();
        this.updateTotalPrice();
      });
    }

    // Overage dropdown event listeners
    if (this.overageDropdownTrigger) {
      this.overageDropdownTrigger.addEventListener('click', () => this.toggleOverageDropdown());
      this.overageDropdownTrigger.addEventListener('keydown', (e) => this.handleDropdownTriggerKeydown(e));
    }

    if (this.overageDropdownMenu) {
      this.overageDropdownMenu.querySelectorAll('.sticky-atc__dropdown-item').forEach(item => {
        item.addEventListener('click', (e) => this.handleOverageSelection(e));
        item.addEventListener('keydown', (e) => this.handleDropdownItemKeydown(e));
      });
    }

    // Close dropdown when clicking outside
    document.addEventListener('click', (e) => this.handleClickOutside(e));

    // Close dropdown on Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.closeOverageDropdown();
      }
    });

    // Sync from main overage dropdown when it changes
    this.setupMainOverageSync();

    window.addEventListener('resize', () => this.updateFooterPadding());
  }

  /**
   * Setup listener for variant change events
   */
  setupVariantListener() {
    const productSection = this.findProductSection();
    if (!productSection) {
      // Fallback to document-level listening
      document.addEventListener('variant:update', (e) => this.handleVariantUpdate(e));
      return;
    }

    productSection.addEventListener('variant:update', (e) => this.handleVariantUpdate(e));
  }

  /**
   * Handle variant update events
   * @param {CustomEvent} event
   */
  handleVariantUpdate(event) {
    const { resource, data } = event.detail;

    // Check if this update is for our product
    if (data?.productId && this.productId && data.productId !== this.productId) {
      return;
    }

    if (resource && resource.price !== undefined) {
      // Price is in cents, convert to dollars
      this.variantPrice = resource.price / 100;
      this.updateTotalPrice();
    }
  }

  /**
   * Sync initial quantity from main form
   */
  syncInitialQuantity() {
    if (this.mainQuantityInput && this.quantityInput) {
      const mainValue = parseInt(this.mainQuantityInput.value) || 1;
      this.quantityInput.value = mainValue;
    }
  }

  /**
   * Get current quantity value
   * @returns {number}
   */
  getQuantity() {
    if (this.quantityInput) {
      return parseInt(this.quantityInput.value) || 1;
    }
    if (this.mainQuantityInput) {
      return parseInt(this.mainQuantityInput.value) || 1;
    }
    return 1;
  }

  /**
   * Calculate and update total price display
   */
  updateTotalPrice() {
    if (!this.totalPriceElement) return;

    const quantity = this.getQuantity();
    const total = this.variantPrice * quantity;

    const formattedPrice = `${this.currencySymbol}${this.formatNumber(total)}`;

    this.totalPriceElement.textContent = formattedPrice;
  }

  /**
   * Format number with thousands separator and 2 decimal places
   * @param {number} value
   * @returns {string}
   */
  formatNumber(value) {
    return value.toLocaleString(this.locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  handleStickyButtonClick(e) {
    e.preventDefault();

    const clickedButton = e.currentTarget;
    if (clickedButton.disabled) return;

    if (this.buttonTextType === 'make_selection') {
      this.scrollToTarget();
    } else {
      this.addToCart(clickedButton);
    }
  }

  handleQuantityChange(change) {
    if (!this.quantityInput) return;

    let currentVal = parseInt(this.quantityInput.value) || 1;
    let newVal = Math.max(1, currentVal + change);

    this.quantityInput.value = newVal;

    // Sync to main quantity input if it exists
    if (this.mainQuantityInput) {
      this.mainQuantityInput.value = newVal;
      this.mainQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    }

    this.updateUnitText(newVal);
    this.updateTotalPrice();
  }

  syncQuantityToMain() {
    if (!this.mainQuantityInput) {
      this.updateUnitText(this.quantityInput.value);
      return;
    }
    this.mainQuantityInput.value = this.quantityInput.value;
    this.mainQuantityInput.dispatchEvent(new Event('input', { bubbles: true }));
    this.updateUnitText(this.quantityInput.value);
  }

  syncQuantityFromMain() {
    if (!this.quantityInput) return;
    this.quantityInput.value = this.mainQuantityInput.value;
    this.updateUnitText(this.mainQuantityInput.value);
  }

  /**
   * Toggle the overage dropdown open/closed
   */
  toggleOverageDropdown() {
    if (!this.overageDropdownMenu || !this.overageDropdownTrigger) return;

    const isOpen = this.overageDropdownMenu.classList.contains('is-open');

    if (isOpen) {
      this.closeOverageDropdown();
    } else {
      this.overageDropdownMenu.classList.add('is-open');
      this.overageDropdownTrigger.setAttribute('aria-expanded', 'true');
    }
  }

  /**
   * Close the overage dropdown
   */
  closeOverageDropdown() {
    if (!this.overageDropdownMenu || !this.overageDropdownTrigger) return;

    this.overageDropdownMenu.classList.remove('is-open');
    this.overageDropdownTrigger.setAttribute('aria-expanded', 'false');
  }

  /**
   * Handle keydown on dropdown trigger
   */
  handleDropdownTriggerKeydown(e) {
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.toggleOverageDropdown();
        if (this.overageDropdownMenu?.classList.contains('is-open')) {
          const firstItem = this.overageDropdownMenu.querySelector('.sticky-atc__dropdown-item');
          firstItem?.focus();
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!this.overageDropdownMenu?.classList.contains('is-open')) {
          this.toggleOverageDropdown();
        }
        const firstItem = this.overageDropdownMenu?.querySelector('.sticky-atc__dropdown-item');
        firstItem?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!this.overageDropdownMenu?.classList.contains('is-open')) {
          this.toggleOverageDropdown();
        }
        const items = this.overageDropdownMenu?.querySelectorAll('.sticky-atc__dropdown-item');
        items?.[items.length - 1]?.focus();
        break;
    }
  }

  /**
   * Handle keydown on dropdown items
   */
  handleDropdownItemKeydown(e) {
    const items = Array.from(this.overageDropdownMenu?.querySelectorAll('.sticky-atc__dropdown-item') || []);
    const currentIndex = items.indexOf(e.target);

    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        this.handleOverageSelection(e);
        this.overageDropdownTrigger?.focus();
        break;
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = (currentIndex + 1) % items.length;
        items[nextIndex]?.focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = (currentIndex - 1 + items.length) % items.length;
        items[prevIndex]?.focus();
        break;
      case 'Escape':
        e.preventDefault();
        this.closeOverageDropdown();
        this.overageDropdownTrigger?.focus();
        break;
      case 'Tab':
        this.closeOverageDropdown();
        break;
    }
  }

  /**
   * Handle click outside to close dropdown
   */
  handleClickOutside(e) {
    if (!this.overageDropdown) return;

    if (!this.overageDropdown.contains(e.target)) {
      this.closeOverageDropdown();
    }
  }

  /**
   * Handle overage selection from sticky dropdown
   */
  handleOverageSelection(e) {
    const item = e.currentTarget;
    const value = item.dataset.value;
    const text = item.textContent;

    // Update sticky dropdown UI
    this.updateOverageDropdownUI(value, text);

    // Close dropdown
    this.closeOverageDropdown();

    // Sync to main quantity calculator
    this.syncOverageToMain(value);
  }

  /**
   * Update the sticky overage dropdown UI
   */
  updateOverageDropdownUI(value, text) {
    if (!this.overageDropdownValue || !this.overageDropdownMenu) return;

    // Update displayed value
    this.overageDropdownValue.textContent = text;

    // Update aria-selected on items
    this.overageDropdownMenu.querySelectorAll('.sticky-atc__dropdown-item').forEach(item => {
      item.setAttribute('aria-selected', item.dataset.value === value ? 'true' : 'false');
    });
  }

  /**
   * Sync overage selection to main quantity calculator
   */
  syncOverageToMain(value) {
    if (!this.mainOverageDropdown) return;

    const { items } = this.mainOverageDropdown;

    // Find and click the matching item in main dropdown
    items.forEach(item => {
      if (item.dataset.value === value) {
        item.click();
      }
    });
  }

  /**
   * Setup listener for main overage dropdown changes
   */
  setupMainOverageSync() {
    if (!this.mainOverageDropdown) return;

    const { items } = this.mainOverageDropdown;

    items.forEach(item => {
      item.addEventListener('click', () => {
        const value = item.dataset.value;
        const text = item.textContent;
        this.updateOverageDropdownUI(value, text);
      });
    });

    // Sync initial value
    this.syncOverageFromMain();
  }

  /**
   * Sync overage value from main quantity calculator to sticky
   */
  syncOverageFromMain() {
    if (!this.mainOverageDropdown || !this.overageDropdownValue) return;

    const { value: mainValue } = this.mainOverageDropdown;
    if (mainValue) {
      const text = mainValue.textContent;
      // Find the matching value from our items
      const matchingItem = this.overageDropdownMenu?.querySelector(
        `.sticky-atc__dropdown-item[aria-selected="true"], .sticky-atc__dropdown-item`
      );

      if (matchingItem) {
        // Find item with matching text
        this.overageDropdownMenu.querySelectorAll('.sticky-atc__dropdown-item').forEach(item => {
          if (item.textContent.trim() === text.trim()) {
            this.updateOverageDropdownUI(item.dataset.value, item.textContent);
          }
        });
      }
    }
  }

  /**
   * Update the unit text display (Box/Boxes, Sheet/Sheets, etc.) with coverage
   * @param {number|string} quantity
   */
  updateUnitText(quantity) {
    const unitElement = this.querySelector('.sticky-atc__unit');
    if (!unitElement) return;

    const qty = parseInt(quantity) || 0;
    if (qty === 0) {
      unitElement.textContent = '';
      return;
    }

    const unitType = unitElement.dataset.unit || this.unitType;
    const coverageSqft = parseFloat(this.dataset.coverageSqft) || 0;

    let unitText = '';
    switch (unitType) {
      case 'box':
        unitText = qty === 1 ? this.translations.box : this.translations.boxes;
        break;
      case 'sheet':
        unitText = qty === 1 ? this.translations.sheet : this.translations.sheets;
        break;
      case 'piece':
        unitText = qty === 1 ? this.translations.piece : this.translations.pieces;
        break;
      default:
        unitText = '';
    }

    if (coverageSqft > 0 && (unitType === 'box' || unitType === 'sheet')) {
      const totalCoverage = (coverageSqft * qty).toFixed(2);
      unitText += ` (${totalCoverage} ${this.translations.sqFt})`;
    }

    unitElement.textContent = unitText;
  }

  addToCart(clickedButton) {
    if (!this.mainAddToCartButton) return;

    this.animateButtonText(clickedButton);
    this.animateFlyToCart(clickedButton);

    // Get the form and submit it properly
    const form = this.mainAddToCartButton.closest('form');
    if (form) {
      // Ensure quantity is set in the form before submitting
      const quantity = this.getQuantity();
      let quantityInput = form.querySelector('input[name="quantity"]');

      if (!quantityInput) {
        // Create a hidden quantity input if one doesn't exist
        quantityInput = document.createElement('input');
        quantityInput.type = 'hidden';
        quantityInput.name = 'quantity';
        form.appendChild(quantityInput);
      }

      quantityInput.value = quantity;

      // Dispatch submit event which product-form-component listens to
      form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
    } else {
      // Fallback to clicking the button
      this.mainAddToCartButton.click();
    }
  }

  animateButtonText(button) {
    if (this.animationTimeout) clearTimeout(this.animationTimeout);
    if (this.cleanupTimeout) clearTimeout(this.cleanupTimeout);

    if (!button.classList.contains('atc-added')) {
      button.classList.add('atc-added');
    }

    this.animationTimeout = setTimeout(() => {
      this.cleanupTimeout = setTimeout(() => {
        button.classList.remove('atc-added');
      }, 10);
    }, StickyAddToCart.ANIMATION_DURATION);
  }

  animateFlyToCart(clickedButton) {
    const addToCartComponent = this.findAddToCartComponent();
    if (!addToCartComponent) return;

    const image = addToCartComponent.dataset.productVariantMedia;
    const animationEnabled = addToCartComponent.dataset.addToCartAnimation === 'true';
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (!cartIcon || !image || !animationEnabled) return;

    if (!customElements.get('fly-to-cart')) {
      console.warn('fly-to-cart custom element not found');
      return;
    }

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = clickedButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  scrollToTarget() {
    let targetElement = document.querySelector(this.scrollTargetSelector);

    if (!targetElement && this.mainAddToCartButton) {
      targetElement = this.mainAddToCartButton;
    }

    if (!targetElement) return;

    let elementTop = 0;
    let element = targetElement;

    while (element) {
      elementTop += element.offsetTop;
      element = element.offsetParent;
    }

    const headerHeight = document.querySelector('#header-group')?.offsetHeight || 0;
    const offset = headerHeight + 100;
    const targetY = elementTop - offset;

    window.scrollTo({
      top: Math.max(0, targetY),
      behavior: 'smooth'
    });
  }

  updateFooterPadding() {
    if (window.innerWidth >= 750) {
      const footer = this.findFooter();
      if (footer) footer.style.paddingBottom = '';
      return;
    }

    const footer = this.findFooter();
    if (!footer) return;

    const isStickyVisible = this.stickyContainer.classList.contains('is-visible');

    if (isStickyVisible) {
      const rect = this.stickyContainer.getBoundingClientRect();
      const stickyHeight = rect.height || this.stickyContainer.offsetHeight;
      footer.style.paddingBottom = stickyHeight + 'px';
    } else {
      footer.style.paddingBottom = '';
    }
  }

  updateButtonState() {
    if (!this.mainAddToCartButton || !this.stickyButtons || this.stickyButtons.length === 0) return;

    const isDisabled = this.mainAddToCartButton.disabled ||
                      this.mainAddToCartButton.getAttribute('disabled') === 'true';

    this.stickyButtons.forEach(button => {
      button.disabled = isDisabled;

      if (isDisabled) {
        button.setAttribute('aria-disabled', 'true');
      } else {
        button.removeAttribute('aria-disabled');
      }
    });
  }

  updateVisibility(shouldShow) {
    if (shouldShow === this.isVisible) return;

    this.isVisible = shouldShow;

    if (this.isVisible) {
      this.stickyContainer.classList.add('is-visible');
      this.updateButtonState();
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.updateFooterPadding());
      });
    } else {
      this.stickyContainer.classList.remove('is-visible');
      this.updateFooterPadding();
    }
  }

  setupIntersectionObserver() {
    if (!this.mainAddToCartButton) return;

    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const rect = entry.boundingClientRect;
        // Button is above viewport (user has scrolled past it)
        const isAboveViewport = rect.bottom < 0;

        // Only show sticky when button has scrolled above the viewport
        this.updateVisibility(isAboveViewport);
      });
    }, {
      threshold: 0,
      rootMargin: '0px'
    });

    this.intersectionObserver.observe(this.mainAddToCartButton);

    // On initial load, check if button is above viewport (page loaded scrolled down)
    // Defer to rAF to avoid forced layout/reflow during connectedCallback
    requestAnimationFrame(() => {
      const rect = this.mainAddToCartButton.getBoundingClientRect();
      const isAboveViewport = rect.bottom < 0;
      if (isAboveViewport) {
        this.updateVisibility(true);
      }
    });
  }

  setupMutationObserver() {
    if (!this.mainAddToCartButton) return;

    this.mutationObserver = new MutationObserver(() => {
      this.updateButtonState();
    });

    this.mutationObserver.observe(this.mainAddToCartButton, {
      attributes: true,
      attributeFilter: ['disabled']
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  setupResizeObserver() {
    if (!this.stickyContainer) return;

    this.resizeObserver = new ResizeObserver(() => {
      this.updateFooterPadding();
    });

    this.resizeObserver.observe(this.stickyContainer);
  }

  cleanup() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
    if (this.cleanupTimeout) {
      clearTimeout(this.cleanupTimeout);
    }

    // Remove variant listener
    const productSection = this.findProductSection();
    if (productSection) {
      productSection.removeEventListener('variant:update', this.handleVariantUpdate);
    }
    document.removeEventListener('variant:update', this.handleVariantUpdate);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}

export default StickyAddToCart;
